/**
 * Query key factory for consistent and type-safe React Query keys.
 *
 * Provides a standardized way to create query keys across the application,
 * preventing key collisions and making it easier to invalidate related queries.
 *
 * @example
 * ```typescript
 * // Create keys for a feature
 * const entityKeys = createQueryKeyFactory('entity');
 *
 * // Use in queries
 * useQuery({
 *   queryKey: entityKeys.detail(entityId),
 *   queryFn: () => fetchEntity(entityId),
 * });
 *
 * // Invalidate all entity queries
 * queryClient.invalidateQueries({ queryKey: entityKeys.all });
 *
 * // Invalidate specific entity
 * queryClient.invalidateQueries({ queryKey: entityKeys.detail(entityId) });
 * ```
 */

export interface QueryKeyFactory<TKeys extends string = string> {
  /**
   * Base key for all queries of this type.
   * Use this to invalidate all related queries.
   */
  all: readonly [TKeys];

  /**
   * Key for list queries.
   * @param filters - Optional filters to differentiate list queries
   */
  list: (
    filters?: Record<string, unknown>
  ) => readonly [TKeys, 'list', Record<string, unknown>?];

  /**
   * Key for detail queries.
   * @param id - Entity ID
   */
  detail: (id: string | number) => readonly [TKeys, 'detail', string | number];

  /**
   * Key for detail queries with additional params.
   * @param id - Entity ID
   * @param params - Additional parameters
   */
  detailWithParams: (
    id: string | number,
    params: Record<string, unknown>
  ) => readonly [TKeys, 'detail', string | number, Record<string, unknown>];

  /**
   * Custom key builder for specific use cases.
   * @param parts - Additional key parts
   */
  custom: (...parts: unknown[]) => readonly [TKeys, ...unknown[]];
}

/**
 * Create a query key factory for a specific resource type.
 *
 * @param baseKey - Base key for this resource (e.g., 'users', 'entity', 'crm-objects')
 * @returns Query key factory with standardized key builders
 *
 * @example
 * ```typescript
 * const userKeys = createQueryKeyFactory('users');
 *
 * // All users queries
 * userKeys.all // ['users']
 *
 * // List with filters
 * userKeys.list({ role: 'admin' }) // ['users', 'list', { role: 'admin' }]
 *
 * // Single user
 * userKeys.detail(123) // ['users', 'detail', 123]
 *
 * // User with params
 * userKeys.detailWithParams(123, { include: 'profile' })
 * // ['users', 'detail', 123, { include: 'profile' }]
 *
 * // Custom keys
 * userKeys.custom('avatar', 123) // ['users', 'avatar', 123]
 * ```
 */
export function createQueryKeyFactory<TKey extends string>(
  baseKey: TKey
): QueryKeyFactory<TKey> {
  return {
    all: [baseKey] as const,

    list: (filters?: Record<string, unknown>) => {
      if (filters) {
        return [baseKey, 'list', filters] as const;
      }
      return [baseKey, 'list'] as const;
    },

    detail: (id: string | number) => {
      return [baseKey, 'detail', id] as const;
    },

    detailWithParams: (
      id: string | number,
      params: Record<string, unknown>
    ) => {
      return [baseKey, 'detail', id, params] as const;
    },

    custom: (...parts: unknown[]) => {
      return [baseKey, ...parts] as const;
    },
  };
}

/**
 * Predefined query key factories for common resources.
 *
 * These can be imported directly instead of creating factories manually.
 *
 * @example
 * ```typescript
 * import { queryKeys } from '@/lib/utils/queryKeyFactory';
 *
 * useQuery({
 *   queryKey: queryKeys.entity.detail(entityId),
 *   queryFn: () => fetchEntity(entityId),
 * });
 * ```
 */
export const queryKeys = {
  entity: createQueryKeyFactory('entity'),
  entityEvents: createQueryKeyFactory('entity-events'),
  orgUsers: createQueryKeyFactory('org-users'),
  objectLabels: createQueryKeyFactory('object-labels'),
  crmObjects: createQueryKeyFactory('crm-objects'),
  contacts: createQueryKeyFactory('contacts'),
  companies: createQueryKeyFactory('companies'),
  deals: createQueryKeyFactory('deals'),
} as const;

/**
 * Deep equality check for query key parts (handles objects and arrays).
 */
function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (typeof a !== typeof b) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((val, idx) => isEqual(val, b[idx]));
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a as object);
    const bKeys = Object.keys(b as object);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Deep equality comparison requires accessing arbitrary object properties
    return aKeys.every(key => isEqual((a as any)[key], (b as any)[key]));
  }

  return false;
}

/**
 * Helper to check if a query key matches a factory's base.
 *
 * Useful for filtering or matching query keys. Uses deep equality
 * for object comparison to handle filter objects correctly.
 *
 * @example
 * ```typescript
 * const key = ['entity', 'detail', '123'];
 * if (matchesQueryKey(key, queryKeys.entity.all)) {
 *   console.log('This is an entity query');
 * }
 * ```
 */
export function matchesQueryKey(
  key: readonly unknown[],
  baseKey: readonly unknown[]
): boolean {
  if (baseKey.length > key.length) {
    return false;
  }

  for (let i = 0; i < baseKey.length; i++) {
    if (!isEqual(key[i], baseKey[i])) {
      return false;
    }
  }

  return true;
}
